import { date } from 'quasar'
import { useI18n } from 'vue-i18n'

/** Parse API / input calendar dates without UTC day-shift (YYYY-MM-DD). */
export function coerceToLocalDate(value) {
  if (value == null || value === '') return null
  if (value instanceof Date) return value
  const str = String(value).trim()
  const ymd = str.match(/^(\d{4})-(\d{2})-(\d{2})/)
  const isCalendarDay = /^\d{4}-\d{2}-\d{2}$/.test(str)
    || /^\d{4}-\d{2}-\d{2}T00:00:00(\.\d+)?Z?$/i.test(str)
  if (ymd && isCalendarDay) {
    const y = Number(ymd[1])
    const m = Number(ymd[2]) - 1
    const d = Number(ymd[3])
    return new Date(y, m, d)
  }
  const parsed = new Date(str)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

export function useDateFormatter(formatString = null, localeOverride = null) {
  const { locale } = useI18n()
  const effectiveLocale = localeOverride ?? locale.value ?? 'en'

  const formatDate = (value) => {
    if (!value) return ''

    // If a format string is provided, use Quasar's date formatting
    if (formatString) {
      return date.formatDate(value, formatString)
    }

    // Use locale-aware date formatting
    const dateObj = coerceToLocalDate(value)
    if (!dateObj) return ''
    return dateObj.toLocaleDateString(effectiveLocale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  const formatTime = (value) => {
    if (!value) return ''

    // If a format string is provided, use Quasar's date formatting
    if (formatString) {
      return date.formatDate(value, formatString)
    }

    // Use locale-aware time formatting with 12-hour format
    const dateObj = new Date(value)
    return dateObj.toLocaleTimeString(effectiveLocale, {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    })
  }

  const formatDateTime = (value) => {
    if (!value) return ''

    // If a format string is provided, use Quasar's date formatting
    if (formatString) {
      return date.formatDate(value, formatString)
    }

    // Use locale-aware date and time formatting
    const dateObj = coerceToLocalDate(value) ?? new Date(value)
    return dateObj.toLocaleString(effectiveLocale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    })
  }

  const formatDateShort = (value) => {
    if (!value) return ''

    const dateObj = coerceToLocalDate(value)
    if (!dateObj) return ''
    return dateObj.toLocaleDateString(effectiveLocale, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    })
  }

  const formatTimeShort = (value) => {
    if (!value) return ''

    // Short time format (e.g., "2:30 PM")
    const dateObj = new Date(value)
    return dateObj.toLocaleTimeString(effectiveLocale, {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    })
  }

  return {
    formatDate,
    formatTime,
    formatDateTime,
    formatDateShort,
    formatTimeShort
  }
}
